// Line Items CRUD, Materials, and Bulk Operations APIs
import { getApiClient, withOrgConfig } from '../quote-api-helpers'
import type {
  QuoteLineItem, LineItemMaterial,
  CreateLineItemRequest, UpdateLineItemRequest,
} from '../quote-api-types'
import type { QuickAddTierRateRequest } from '../quote-api-ai-types'

export const lineItemsApi = {
  // =========================================================================
  // LINE ITEM CRUD
  // =========================================================================

  async createLineItem(quoteId: string, sectionId: string, data: CreateLineItemRequest) {
    const client = getApiClient()
    const res = await client.post<QuoteLineItem>(
      `/quotebuilder/quotes/${quoteId}/sections/${sectionId}/items/`,
      data,
      withOrgConfig()
    )
    return res.data
  },

  async updateLineItem(quoteId: string, sectionId: string, itemId: string, data: UpdateLineItemRequest) {
    const client = getApiClient()
    const res = await client.patch<QuoteLineItem>(
      `/quotebuilder/quotes/${quoteId}/sections/${sectionId}/items/${itemId}/`,
      data,
      withOrgConfig()
    )
    return res.data
  },

  async deleteLineItem(quoteId: string, sectionId: string, itemId: string) {
    const client = getApiClient()
    await client.delete(`/quotebuilder/quotes/${quoteId}/sections/${sectionId}/items/${itemId}/`, withOrgConfig())
  },

  // =========================================================================
  // MATERIAL CRUD
  // =========================================================================

  async addMaterial(quoteId: string, sectionId: string, itemId: string, data: Record<string, unknown>) {
    const client = getApiClient()
    const res = await client.post<LineItemMaterial>(
      `/quotebuilder/quotes/${quoteId}/sections/${sectionId}/items/${itemId}/add_material/`,
      data,
      withOrgConfig()
    )
    return res.data
  },

  async updateMaterial(quoteId: string, sectionId: string, itemId: string, materialId: string, data: Record<string, unknown>) {
    const client = getApiClient()
    const res = await client.patch<LineItemMaterial>(
      `/quotebuilder/quotes/${quoteId}/sections/${sectionId}/items/${itemId}/update_material/${materialId}/`,
      data,
      withOrgConfig()
    )
    return res.data
  },

  async deleteMaterial(quoteId: string, sectionId: string, itemId: string, materialId: string) {
    const client = getApiClient()
    await client.delete(
      `/quotebuilder/quotes/${quoteId}/sections/${sectionId}/items/${itemId}/remove_material/${materialId}/`,
      withOrgConfig()
    )
  },

  async quickAddTierRate(quoteId: string, sectionId: string, data: QuickAddTierRateRequest) {
    const client = getApiClient()
    const res = await client.post<QuoteLineItem>(
      `/quotebuilder/quotes/${quoteId}/sections/${sectionId}/items/quick_add/`,
      data,
      withOrgConfig()
    )
    return res.data
  },

  async applyApplication(quoteId: string, sectionId: string, itemId: string, applicationId: string) {
    const client = getApiClient()
    const res = await client.post<QuoteLineItem>(
      `/quotebuilder/quotes/${quoteId}/sections/${sectionId}/items/${itemId}/apply_application/`,
      { application_id: applicationId },
      withOrgConfig()
    )
    return res.data
  },

  // =========================================================================
  // BULK OPERATIONS (Spreadsheet-style batching)
  // =========================================================================

  /**
   * Bulk update multiple line items in a single transaction.
   * Backend returns items with their data in `{ id, data: QuoteLineItem }` format.
   */
  async bulkUpdateLineItems(
    quoteId: string,
    sectionId: string,
    updates: Array<{ id: string; patch: Record<string, unknown> }>
  ) {
    const client = getApiClient()
    const res = await client.post<{
      updated: Array<{ id: string; data: QuoteLineItem }>
      errors: Array<{ id: string; field?: string; error: string }>
      quote_totals: { subtotal: string; gst: string; total_incl_gst: string }
    }>(
      `/quotebuilder/quotes/${quoteId}/sections/${sectionId}/items/bulk_update/`,
      { updates },
      withOrgConfig()
    )
    return res.data
  },

  /**
   * Bulk create multiple line items in a single transaction.
   * Backend returns items with their data in `{ index, id, data: QuoteLineItem }` format.
   */
  async bulkCreateLineItems(
    quoteId: string,
    sectionId: string,
    items: Array<{
      description: string
      quantity?: number | string
      unit?: string
      location?: string
      product_system?: string
      productivity_pmpd?: number | string
      sort_order?: number
    }>
  ) {
    const client = getApiClient()
    const res = await client.post<{
      created: Array<{ index: number; id: string; data: QuoteLineItem }>
      errors: Array<{ index: number; errors?: Record<string, string[]>; error?: string }>
      quote_totals: { subtotal: string; gst: string; total_incl_gst: string }
    }>(
      `/quotebuilder/quotes/${quoteId}/sections/${sectionId}/items/bulk_create/`,
      { items },
      withOrgConfig()
    )
    return res.data
  },

  /**
   * Bulk quick-add from tier rates or applications.
   */
  async bulkQuickAdd(
    quoteId: string,
    sectionId: string,
    items: Array<{
      tier_rate_id?: string
      application_id?: string
      quantity: number | string
      location?: string
      tier?: 'tier1' | 'tier2' | 'tier3'
    }>
  ) {
    const client = getApiClient()
    const res = await client.post<{
      created: Array<{ index: number; id: string; data: QuoteLineItem }>
      errors: Array<{ index: number; errors?: Record<string, string[]>; error?: string }>
      quote_totals: { subtotal: string; gst: string; total_incl_gst: string }
    }>(
      `/quotebuilder/quotes/${quoteId}/sections/${sectionId}/items/bulk_quick_add/`,
      { items },
      withOrgConfig()
    )
    return res.data
  },

  /**
   * Bulk delete multiple line items in a single transaction.
   */
  async bulkDeleteLineItems(quoteId: string, sectionId: string, ids: string[]) {
    const client = getApiClient()
    const res = await client.post<{
      deleted: string[]
      errors: Array<{ id: string; error: string }>
      quote_totals: { subtotal: string; gst: string; total_incl_gst: string }
    }>(
      `/quotebuilder/quotes/${quoteId}/sections/${sectionId}/items/bulk_delete/`,
      { ids },
      withOrgConfig()
    )
    return res.data
  },
}
